import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useRegisterSW } from "virtual:pwa-register/react";
import { setUpdateReloadPending } from "@/lib/update-reload";

/** How often a long-lived tab asks the server whether a newer build exists. */
export const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Id of the first update toast. A prompt replaces the visible toast in place
 * instead of stacking; once a toast is answered, the next prompt takes a fresh
 * id (see `toastIdFor`).
 */
export const UPDATE_TOAST_ID = "app-update-available";

/**
 * A toast that was just answered keeps animating out for a moment, and sonner
 * merges a toast with the same id into it, so a prompt shown again in that
 * window (an accepted update abandoned at once) would leave with it. Each
 * answered toast therefore retires its id.
 */
function toastIdFor(generation: number): string {
  return generation === 0 ? UPDATE_TOAST_ID : `${UPDATE_TOAST_ID}-${generation}`;
}

/**
 * How long an update reload may leave this document alive before the hook
 * treats it as cancelled. A `beforeunload` guard, such as the one for unsaved
 * settings, lets the user stay on the page.
 */
export const UPDATE_RELOAD_TIMEOUT_MS = 5_000;

/**
 * How long an accepted worker may take to activate before the hook gives up on
 * the switch, so a stalled activation cannot keep stale-chunk recovery stood
 * down for the rest of the session.
 */
export const UPDATE_ACTIVATION_TIMEOUT_MS = 30_000;

interface RegisteredWorker {
  readonly swUrl: string;
  readonly registration: ServiceWorkerRegistration;
}

/**
 * What the update toast offers. `available`: a newer build is installed and
 * this tab's user can switch to it. `applied`: the user of another tab already
 * switched, so this tab runs an old build under the new worker until its user
 * reloads.
 */
type UpdatePrompt = "available" | "applied";

/**
 * Asks the browser to look for a newer service worker, but only when the
 * network demonstrably serves the worker script.
 *
 * The script is fetched first, uncached, and `registration.update()` runs only
 * on a 200. An offline tab, a captive portal, or a deploy that is mid-flight
 * and answering with an error page would otherwise make `update()` fail its
 * script fetch, and a non-OK answer for the registered script can discard the
 * registration. Skipping the update costs one missed check; the next trigger
 * tries again.
 */
export async function checkForUpdate(
  swUrl: string,
  registration: ServiceWorkerRegistration,
): Promise<void> {
  if (registration.installing || !navigator.onLine) {
    return;
  }
  try {
    const response = await fetch(swUrl, {
      cache: "no-store",
      headers: { "cache-control": "no-cache" },
    });
    if (response.status === 200) {
      await registration.update();
    }
  } catch {
    // Offline or a transient network failure: leave the running build alone.
  }
}

/**
 * Reloads for an update and tells stale-chunk recovery the reload is on its
 * way, so a route chunk that fails meanwhile does not start a second one.
 *
 * A reload the user cancels leaves this document running its old build, whose
 * precache the new worker may already have deleted. If the page has not begun
 * to unload (`pagehide`) when the timeout elapses, recovery is handed back and
 * `onCancelled` runs, so the user can reload later.
 */
function reloadPage(onCancelled: () => void): void {
  setUpdateReloadPending(true);
  let unloading = false;
  const onPageHide = () => {
    unloading = true;
  };
  window.addEventListener("pagehide", onPageHide, { once: true });
  window.setTimeout(() => {
    window.removeEventListener("pagehide", onPageHide);
    if (!unloading) {
      setUpdateReloadPending(false);
      onCancelled();
    }
  }, UPDATE_RELOAD_TIMEOUT_MS);
  window.location.reload();
}

/**
 * Stands in for the plugin's own reload. Without an `onNeedReload`, the plugin
 * reloads every tab the new worker takes over, so accepting the update in one
 * tab would reload all the others and discard their unsaved input.
 */
function keepPageOnNewController(): void {
  // Intentionally empty: `useAppUpdate` decides which tab reloads, and when.
}

/**
 * Calls `onUpdateInstalled` whenever a worker that would replace the active one
 * finishes installing, and once at the start if such a worker is already
 * waiting. Returns the cleanup.
 *
 * The plugin learns of updates through workbox-window, which follows only the
 * first update it attributes to another trigger (any found more than a minute
 * after registration, such as the hourly check) and then stops listening. If
 * that worker fails to install, every later one installs unannounced. Watching
 * the registration directly keeps the prompt working for the life of the tab.
 *
 * A worker installed while nothing is active is the first install: it is the
 * build this page already runs, so it is not announced.
 */
function watchForInstalledUpdates(
  registration: ServiceWorkerRegistration,
  onUpdateInstalled: () => void,
): () => void {
  const followed = new Map<ServiceWorker, () => void>();

  const followInstalling = () => {
    const worker = registration.installing;
    if (!worker || !registration.active || followed.has(worker)) {
      return;
    }
    const onStateChange = () => {
      if (worker.state === "installing") {
        return;
      }
      worker.removeEventListener("statechange", onStateChange);
      followed.delete(worker);
      // A failed install ends `redundant`. Any other state means it installed,
      // even if it has already moved on to activating in a tab nothing controls.
      if (worker.state !== "redundant") {
        onUpdateInstalled();
      }
    };
    worker.addEventListener("statechange", onStateChange);
    followed.set(worker, onStateChange);
  };

  if (registration.waiting && registration.active) {
    onUpdateInstalled();
  }
  followInstalling();
  registration.addEventListener("updatefound", followInstalling);

  return () => {
    registration.removeEventListener("updatefound", followInstalling);
    for (const [worker, onStateChange] of followed) {
      worker.removeEventListener("statechange", onStateChange);
    }
    followed.clear();
  };
}

/**
 * Registers the service worker and lets the operator choose when to switch to
 * a newer build.
 *
 * Updates are user-accepted, and a tab reloads only when its own user chooses
 * Reload. The new worker installs in the background and waits, and the hook
 * shows a persistent "New version available" toast. Reload then does one of
 * two things:
 *
 * - If a worker is waiting, it posts `SKIP_WAITING` and reloads this tab once
 *   that worker has activated. This works whether or not the worker controls
 *   the tab.
 * - If nothing is waiting, the new build is already active, and a plain reload
 *   loads it. This happens in a tab the worker does not control, where an
 *   update has no clients to wait for and activates at once.
 *
 * Activation hands every other controlled tab to the new worker. Those tabs do
 * not reload. Their toast becomes "Xan was updated", whose Reload is a plain
 * reload, so unsaved input survives until each user chooses. A route chunk such
 * a tab can no longer load is left to the stale-chunk guard in `lazy-route.ts`,
 * which reloads once when the user navigates.
 *
 * A tab that stays open for days would otherwise only learn of a deploy from
 * the browser's own navigation-time check, so the hook also re-checks hourly
 * and whenever the tab becomes visible again (the way an installed app resumes
 * from the background).
 *
 * Mount once, at the app root. In `vp dev` the registration module is a no-op,
 * so nothing registers and no prompt appears.
 */
export function useAppUpdate(): void {
  const [worker, setWorker] = useState<RegisteredWorker | null>(null);
  const [updateApplied, setUpdateApplied] = useState(false);
  // Set when this tab's user accepts, so the takeover that follows reloads
  // this tab instead of reporting an update made somewhere else.
  const acceptedRef = useRef(false);
  // Counts answered toasts, so each prompt after an answer takes a fresh id.
  // State rather than a ref: an update abandoned at once is cleared and
  // offered again in one batch, and only the new generation re-shows it.
  const [toastGeneration, setToastGeneration] = useState(0);
  const toastGenerationRef = useRef(0);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      if (registration) {
        setWorker({ swUrl, registration });
      }
    },
    onNeedReload: keepPageOnNewController,
  });

  useEffect(() => {
    if (!worker) {
      return;
    }
    const { swUrl, registration } = worker;
    const check = () => {
      void checkForUpdate(swUrl, registration);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        check();
      }
    };
    const interval = window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [worker]);

  useEffect(() => {
    if (!worker) {
      return;
    }
    return watchForInstalledUpdates(worker.registration, () => {
      setNeedRefresh(true);
    });
  }, [worker, setNeedRefresh]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }
    const container = navigator.serviceWorker;
    let controlled = container.controller !== null;
    const onControllerChange = () => {
      const wasControlled = controlled;
      controlled = container.controller !== null;
      // A first takeover of an uncontrolled tab is not an update.
      if (wasControlled && !acceptedRef.current) {
        setUpdateApplied(true);
      }
    };
    container.addEventListener("controllerchange", onControllerChange);
    return () => {
      container.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  // Every answer consumes the toast. Closing it defers only the build or
  // takeover it announced, and Reload replaces it with the reload. Clearing
  // both lets the next newly installed worker or takeover announce again; a
  // re-check that finds the same waiting worker reports nothing new.
  const clearPrompt = useCallback(() => {
    setToastGeneration((generation) => generation + 1);
    setNeedRefresh(false);
    setUpdateApplied(false);
  }, [setNeedRefresh]);

  const reloadForUpdate = useCallback(() => {
    reloadPage(() => {
      // The user stayed, so this tab accepts nothing any more and runs the old
      // build under the new worker until they reload.
      acceptedRef.current = false;
      setUpdateApplied(true);
    });
  }, []);

  const acceptUpdate = useCallback(
    (registration: ServiceWorkerRegistration | undefined) => {
      const waiting = registration?.waiting ?? null;
      // With nothing waiting, another tab may have accepted this build a moment
      // ago: reloading under the outgoing worker would miss it, so wait too.
      const activating = registration?.active?.state === "activating" ? registration.active : null;
      const incoming = waiting ?? activating;
      if (!incoming) {
        reloadForUpdate();
        return;
      }
      acceptedRef.current = true;
      // From here the new worker can take over and delete the old precache
      // before this tab reloads, so stale-chunk recovery stands down now.
      setUpdateReloadPending(true);
      let settled = false;
      const stop = () => {
        settled = true;
        window.clearTimeout(stalled);
        incoming.removeEventListener("statechange", onStateChange);
      };
      // The switch never happened: a newer worker replaced this one, posting
      // SKIP_WAITING failed, or activation stalled. This tab keeps its old
      // build, recovery is handed back, and the prompt returns so the user can
      // try again.
      const abandon = () => {
        if (settled) {
          return;
        }
        stop();
        acceptedRef.current = false;
        setUpdateReloadPending(false);
        setNeedRefresh(true);
      };
      function onStateChange() {
        if (incoming?.state === "activated") {
          stop();
          reloadForUpdate();
        } else if (incoming?.state === "redundant") {
          abandon();
        }
      }
      const stalled = window.setTimeout(abandon, UPDATE_ACTIVATION_TIMEOUT_MS);
      incoming.addEventListener("statechange", onStateChange);
      if (waiting) {
        // Posts SKIP_WAITING to the waiting worker. The reload is this hook's.
        updateServiceWorker().catch(abandon);
      }
    },
    [updateServiceWorker, reloadForUpdate, setNeedRefresh],
  );

  const updatePrompt: UpdatePrompt | null = updateApplied
    ? "applied"
    : needRefresh
      ? "available"
      : null;

  useEffect(() => {
    // A prompt updates the visible toast in place. The prompt never times out,
    // because a tab left on an old build needs the choice to stay reachable;
    // the close button still lets the operator defer.
    const generation = toastGeneration;
    toastGenerationRef.current = generation;
    const id = toastIdFor(generation);
    // A retired toast that reports its dismissal late must not clear the
    // prompt that replaced it.
    const onDismiss = () => {
      if (toastGenerationRef.current === generation) {
        clearPrompt();
      }
    };
    if (updatePrompt === "applied") {
      toast("Xan was updated", {
        id,
        description: "Reload when you're ready to use the new version.",
        duration: Number.POSITIVE_INFINITY,
        action: {
          label: "Reload",
          onClick: () => {
            clearPrompt();
            reloadForUpdate();
          },
        },
        onDismiss,
      });
    } else if (updatePrompt === "available") {
      toast("New version available", {
        id,
        description: "Reload to get the latest changes.",
        duration: Number.POSITIVE_INFINITY,
        action: {
          label: "Reload",
          onClick: () => {
            clearPrompt();
            acceptUpdate(worker?.registration);
          },
        },
        onDismiss,
      });
    }
  }, [updatePrompt, toastGeneration, worker, acceptUpdate, clearPrompt, reloadForUpdate]);

  useEffect(
    () => () => {
      // sonner reports this dismissal through `onDismiss` too, but only after
      // the hook has unmounted, when clearing its state does nothing.
      toast.dismiss(toastIdFor(toastGenerationRef.current));
    },
    [],
  );
}
